import { useState, useRef, useEffect } from 'react'

const QUICK_ACTIONS = [
  'Tell me about your services',
  'AI Chatbot pricing',
  'How long does setup take?',
  'Do I need to rebuild my website?',
 
]

const LIMIT_REPLY = 'This is only for testing purpose, you reached the demo limit. Try again after 24/7. If you want this in your site, contact the team at WhatsApp 9677255272.'

const STORAGE_KEY = 'tredex_chat_messages'

function loadMessages() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw)
    if (Array.isArray(parsed) && parsed.length) return parsed
  } catch {
    // ignore corrupt storage
  }
  return null
}

function saveMessages(messages) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(messages))
  } catch {
    // ignore storage errors
  }
}

export default function FloatingChat() {
  const [isOpen, setIsOpen] = useState(false)
  const [messages, setMessages] = useState(() => loadMessages() || [
    { role: 'assistant', content: 'Welcome to TRED-EX AI! You\'re in the right place. Tell us what AI feature or IT solution you need for your website or business, and we\'ll help you get started.' }
  ])
  const [input, setInput] = useState('')
  const [loading, setLoading] = useState(false)
  const [limitReached, setLimitReached] = useState(false)
  const [error, setError] = useState('')
  const messagesEndRef = useRef(null)
  const [togglePop, setTogglePop] = useState(false)
  const [jiggle, setJiggle] = useState(false)

  useEffect(() => {
    if (!togglePop) return
    const id = setTimeout(() => setTogglePop(false), 260)
    return () => clearTimeout(id)
  }, [togglePop])

  useEffect(() => {
    if (isOpen) return
    const id = setInterval(() => {
      setJiggle(true)
      setTimeout(() => setJiggle(false), 500)
    }, 2000)
    return () => clearInterval(id)
  }, [isOpen])

  const toggleChat = () => {
    setTogglePop(true)
    setIsOpen(prev => !prev)
  }

  const resetChat = () => {
    const fresh = [{ role: 'assistant', content: 'Welcome to TRED-EX AI! You\'re in the right place. Tell us what AI feature or IT solution you need for your website or business, and we\'ll help you get started.' }]
    setMessages(fresh)
    setLimitReached(false)
    setError('')
  }

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages])

  useEffect(() => {
    saveMessages(messages)
  }, [messages])

  const handleSend = async (e) => {
    e.preventDefault()
    if (!input.trim() || loading) return

    const userMessage = input.trim()
    setInput('')
    setMessages(prev => [...prev, { role: 'user', content: userMessage }])
    setLoading(true)
    setError('')

    try {
      const res = await fetch('/api/chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: userMessage, history: messages.slice(1) })
      })
      const data = await res.json()
      if (data.reply === LIMIT_REPLY) {
        setLimitReached(true)
      }
      if (!res.ok) {
        setMessages(prev => [...prev, { role: 'assistant', content: data.error || 'Sorry, something went wrong. Please try again.' }])
      } else {
        setMessages(prev => [...prev, { role: 'assistant', content: data.reply }])
      }
    } catch (err) {
      setMessages(prev => [...prev, { role: 'assistant', content: 'Sorry, something went wrong. Please try again.' }])
    } finally {
      setLoading(false)
    }
  }

  const handleQuickAction = async (action) => {
    setMessages(prev => [...prev, { role: 'user', content: action }])
    setLoading(true)
    setError('')

    try {
      const res = await fetch('/api/chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: action, history: messages.slice(1) })
      })
      const data = await res.json()
      if (data.reply === LIMIT_REPLY) {
        setLimitReached(true)
      }
      if (!res.ok) {
        setMessages(prev => [...prev, { role: 'assistant', content: data.error || 'Sorry, something went wrong. Please try again.' }])
      } else {
        setMessages(prev => [...prev, { role: 'assistant', content: data.reply }])
      }
    } catch (err) {
      setMessages(prev => [...prev, { role: 'assistant', content: 'Sorry, something went wrong. Please try again.' }])
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="floating-chat-wrapper">
      <div className={`floating-chat-window${isOpen ? ' is-open' : ''}`}>
          <div className="floating-chat-header">
            <div className="floating-chat-header-info">
              <div className="floating-chat-icon">
                <svg
                  className="smiling-robot"
                  viewBox="0 0 64 64"
                  width="32"
                  height="32"
                  aria-hidden="true"
                >
                  {/* antenna */}
                  <line
                    x1="32"
                    y1="9"
                    x2="32"
                    y2="15"
                    stroke="white"
                    strokeWidth="3"
                    strokeLinecap="round"
                  />

                  <circle
                    cx="32"
                    cy="6"
                    r="3"
                    fill="white"
                  />

                  {/* robot head */}
                  <rect
                    x="12"
                    y="15"
                    width="40"
                    height="34"
                    rx="11"
                    fill="none"
                    stroke="white"
                    strokeWidth="4"
                  />

                  {/* ears */}
                  <rect
                    x="7"
                    y="26"
                    width="6"
                    height="12"
                    rx="3"
                    fill="white"
                  />

                  <rect
                    x="51"
                    y="26"
                    width="6"
                    height="12"
                    rx="3"
                    fill="white"
                  />

                  {/* eyes */}
                  <circle
                    cx="24"
                    cy="29"
                    r="3"
                    fill="white"
                  />

                  <circle
                    cx="40"
                    cy="29"
                    r="3"
                    fill="white"
                  />

                  {/* smile */}
                  <path
                    d="M22 37 Q32 46 42 37"
                    fill="none"
                    stroke="white"
                    strokeWidth="3"
                    strokeLinecap="round"
                  />
                </svg>
              </div>
              <div>
                <h3>TRED-EX AI Assistant</h3>
                <div className="floating-chat-status">
                  <span className="status-dot"></span>
                  <span>Online</span>
                </div>
              </div>
            </div>
            <div className="floating-chat-header-actions">
              <button className="floating-chat-clear" onClick={resetChat} aria-label="Clear chat" title="Clear chat">
                <i className="fas fa-trash-alt"></i>
              </button>
              <button className="floating-chat-close" onClick={() => setIsOpen(false)}>
                <i className="fas fa-times"></i>
              </button>
            </div>
          </div>

          <div className="floating-chat-messages">
            {messages.map((msg, i) => (
              <div key={i} className={`chat-message ${msg.role === 'user' ? 'user-message' : 'ai-message'}`}>
                <div className="message-avatar" style={{ background: 'var(--primary)' }}>
                  <i className={msg.role === 'user' ? 'fas fa-user' : 'fas fa-robot'}></i>
                </div>
                <div className="message-content">
                  <div className="message-text">{msg.content}</div>
                </div>
              </div>
            ))}
            {loading && (
              <div className="chat-message ai-message">
                <div className="message-avatar" style={{ background: 'var(--primary)' }}>
                  <i className="fas fa-robot"></i>
                </div>
                <div className="message-content">
                  <div className="typing-indicator">
                    <span></span><span></span><span></span>
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {messages.length === 1 && !limitReached && (
            <div className="floating-chat-quick-actions">
              {QUICK_ACTIONS.map((action, i) => (
                <button key={i} className="quick-action-chip" onClick={() => handleQuickAction(action)}>
                  {action}
                </button>
              ))}
            </div>
          )}

          <form onSubmit={handleSend} className="floating-chat-input">
            <input
              type="text"
              value={input}
              onChange={e => setInput(e.target.value)}
              placeholder="Type your message..."
              disabled={loading}
            />
            <button type="submit" className="chat-send-btn" disabled={loading || !input.trim()} aria-label="Send message">
              <i className="fas fa-paper-plane"></i>
            </button>
          </form>
        </div>

    <button
  className={`floating-chat-toggle${togglePop ? ' pop' : ''}${jiggle && !isOpen ? ' jiggle' : ''}`}
  onClick={toggleChat}
>
  <span className="floating-chat-toggle-inner">

    <span className="floating-chat-icon-wrap">
      {isOpen ? (
        <i className="fas fa-times"></i>
      ) : (
        <svg
          className="smiling-robot"
          viewBox="0 0 64 64"
          width="36"
          height="36"
          aria-hidden="true"
        >
          {/* antenna */}
          <line
            x1="32"
            y1="9"
            x2="32"
            y2="15"
            stroke="white"
            strokeWidth="3"
            strokeLinecap="round"
          />

          <circle
            cx="32"
            cy="6"
            r="3"
            fill="white"
          />

          {/* robot head */}
          <rect
            x="12"
            y="15"
            width="40"
            height="34"
            rx="11"
            fill="none"
            stroke="white"
            strokeWidth="4"
          />

          {/* ears */}
          <rect
            x="7"
            y="26"
            width="6"
            height="12"
            rx="3"
            fill="white"
          />

          <rect
            x="51"
            y="26"
            width="6"
            height="12"
            rx="3"
            fill="white"
          />

          {/* eyes */}
          <circle
            cx="24"
            cy="29"
            r="3"
            fill="white"
          />

          <circle
            cx="40"
            cy="29"
            r="3"
            fill="white"
          />

          {/* smile */}
          <path
            d="M22 37 Q32 46 42 37"
            fill="none"
            stroke="white"
            strokeWidth="3"
            strokeLinecap="round"
          />
        </svg>
      )}
    </span>

    {!isOpen && (
      <span className="floating-chat-label">
        Ask me
      </span>
    )}

  </span>
</button>
    </div>
  )
}



